import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FieldGroup } from '@/Components/shadcn/field';
import { RHFInput } from '@/Components/shadcn/rhf-input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/Components/shadcn/dialog';
import { Button } from '@/Components/shadcn/button';
import { Spinner } from '@/Components/shadcn/spinner';
import CountrySelector from '@/Components/Address/CountrySelector';
import { useNotificationContext } from '@/Context/NotificationContext';
import { useBillingInfoClient } from '@/Hooks/ClientHooks';
import useCompanyId from '@/Hooks/UseCompanyId';
import BillingInfo, {
    BillingInfoResolverSchema,
    billingInfoValidationScheme,
    EmptyBillingInfo,
} from '@/Models/BillingInfo';
import UIStrings from '@/Utils/UIStrings';

interface EditBillingAddressDialogProps {
    open: boolean;
    onClose: () => void;
    billingInfo: BillingInfo | undefined;
    setBillingInfo: React.Dispatch<React.SetStateAction<BillingInfo | undefined>>;
}

const EditBillingAddressDialog = ({
    open,
    billingInfo = EmptyBillingInfo,
    onClose,
    setBillingInfo,
}: EditBillingAddressDialogProps) => {
    const client = useBillingInfoClient();
    const companyId = useCompanyId();
    const [loading, setLoading] = useState<boolean>(false);
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const form = useForm({
        resolver: yupResolver(billingInfoValidationScheme),
        mode: 'onSubmit',
        defaultValues: billingInfo as BillingInfoResolverSchema,
    });

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const submitBillingInfo = async (values: BillingInfo) => {
        setLoading(true);
        try {
            const updatedBillingInfo = await client.saveBillingInfoAsync(companyId, values);
            setBillingInfo(updatedBillingInfo);
            onClose();
            showSuccessToaster(UIStrings.BillingDataSuccessfullyUpdated);
        } catch {
            showFailToaster();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
            <DialogContent className="max-h-[calc(100vh-theme(spacing.16))] max-w-lg overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="tracking-tight">{UIStrings.BillingAddress}</DialogTitle>
                </DialogHeader>
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(submitBillingInfo)}>
                        <FieldGroup className="gap-5 pt-0 pb-7.5">
                            <RHFInput fullWidth label={UIStrings.BillingTo} name="billingTo" />
                            <CountrySelector label={`${UIStrings.Country}*`} name="country" />
                            <RHFInput fullWidth label={UIStrings.AddressLine1} name="addressLine1" />
                            <RHFInput fullWidth label={UIStrings.AddressLine2} name="addressLine2" />
                            <div className="flex flex-row gap-5">
                                <FieldGroup className="w-full basis-1/2 gap-5">
                                    <RHFInput fullWidth label={UIStrings.City} name="city" />
                                    <RHFInput fullWidth label={UIStrings.Zip} name="postalCode" />
                                </FieldGroup>
                                <RHFInput fullWidth label={UIStrings.State} name="state" fieldClassName="basis-1/2" />
                            </div>
                        </FieldGroup>
                        <DialogFooter className="flex gap-5">
                            <Button
                                variant="outline"
                                type="button"
                                onClick={handleClose}
                                disabled={loading}
                                className="flex-1"
                            >
                                {UIStrings.Cancel}
                            </Button>
                            <Button disabled={loading} type="submit" className="flex-1">
                                {loading && <Spinner className="mr-2 size-4" />}
                                {UIStrings.Save}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

export default EditBillingAddressDialog;
